import type {
  Activity,
  ActivityStatus,
  ActivityType,
  CalendarInfo,
  ConstraintType,
  ParseProgress,
  Relationship,
  RelationshipType,
  ResourceAssignment,
  ResourceInfo,
  ScheduleModel,
  WbsNode,
} from '../types/schedule';
import { HARD_CONSTRAINTS } from '../types/schedule';
import { parseXerDate } from './xerParser';

/**
 * Primavera P6 XML reader, emitting the same ScheduleModel as the XER reader.
 *
 * Uses the browser's built-in DOMParser rather than a userland XML library —
 * this runs client-side only, so there is nothing to gain from a polyfill and
 * a parser dependency would be one more thing consuming untrusted uploads.
 */

const TYPE_MAP: Record<string, ActivityType> = {
  'Task Dependent': 'Task',
  'Start Milestone': 'Milestone',
  'Finish Milestone': 'Finish Milestone',
  'Level of Effort': 'Level of Effort',
  'WBS Summary': 'WBS Summary',
  'Resource Dependent': 'Resource Dependent',
};

const STATUS_MAP: Record<string, ActivityStatus> = {
  'Not Started': 'Not Started',
  'In Progress': 'In Progress',
  Completed: 'Completed',
};

const RELATIONSHIP_MAP: Record<string, RelationshipType> = {
  'Finish to Start': 'FS',
  'Start to Start': 'SS',
  'Finish to Finish': 'FF',
  'Start to Finish': 'SF',
};

const CONSTRAINT_VALUES = new Set<string>([
  'Start On',
  'Start On or Before',
  'Start On or After',
  'Finish On',
  'Finish On or Before',
  'Finish On or After',
  'Mandatory Start',
  'Mandatory Finish',
  'As Late As Possible',
]);

function text(parent: Element, ...tagNames: string[]): string {
  for (const tag of tagNames) {
    // Direct children only — an Activity's <Name> must not pick up a nested
    // ResourceAssignment's <Name>.
    for (let i = 0; i < parent.children.length; i++) {
      const child = parent.children[i];
      if (child.tagName === tag) return child.textContent?.trim() ?? '';
    }
  }
  return '';
}

function num(value: string, fallback = 0): number {
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toConstraint(value: string): ConstraintType {
  return CONSTRAINT_VALUES.has(value) ? (value as ConstraintType) : null;
}

function elements(root: Document | Element, tagName: string): Element[] {
  return Array.from(root.getElementsByTagName(tagName));
}

export async function parseXMLFile(
  file: File,
  onProgress?: ParseProgress
): Promise<ScheduleModel> {
  onProgress?.(5, 'Reading file');
  const raw = await file.text();

  onProgress?.(20, 'Parsing XML');
  const doc = new DOMParser().parseFromString(raw, 'application/xml');

  const parseError = doc.getElementsByTagName('parsererror')[0];
  if (parseError) {
    throw new Error('The XML could not be parsed — the file may be corrupt or not a P6 export.');
  }

  const projectEl = elements(doc, 'Project')[0];
  if (!projectEl) {
    throw new Error('No <Project> element found — this does not look like a P6 XML export.');
  }

  onProgress?.(35, 'Reading calendars and resources');

  const calendars = new Map<string, CalendarInfo>();
  for (const el of elements(doc, 'Calendar')) {
    const id = text(el, 'ObjectId');
    if (!id) continue;
    const hoursPerDay = num(text(el, 'HoursPerDay'), 8);
    calendars.set(id, {
      id,
      name: text(el, 'Name') || 'Unnamed calendar',
      hoursPerDay: hoursPerDay > 0 ? hoursPerDay : 8,
    });
  }

  const resources = new Map<string, ResourceInfo>();
  for (const el of elements(doc, 'Resource')) {
    const id = text(el, 'ObjectId');
    if (!id) continue;
    resources.set(id, {
      id,
      name: text(el, 'Name') || 'Resource',
      shortName: text(el, 'Id'),
      type: text(el, 'ResourceType') || 'Labour',
    });
  }

  const wbs: WbsNode[] = elements(doc, 'WBS')
    .map((el) => ({
      id: text(el, 'ObjectId'),
      parentId: text(el, 'ParentObjectId') || null,
      name: text(el, 'Name') || 'WBS',
      code: text(el, 'Code'),
      sequence: num(text(el, 'SequenceNumber')),
      isProjectNode: false,
    }))
    .filter((node) => node.id);

  onProgress?.(50, 'Reading relationships');

  const relationships: Relationship[] = [];
  for (const el of elements(doc, 'Relationship')) {
    const predecessorId =
      text(el, 'PredecessorActivityObjectId', 'PredecessorObjectId');
    const successorId = text(el, 'SuccessorActivityObjectId', 'SuccessorObjectId');
    if (!predecessorId || !successorId) continue;

    relationships.push({
      predecessorId,
      successorId,
      type: RELATIONSHIP_MAP[text(el, 'Type')] ?? 'FS',
      lagHours: num(text(el, 'Lag')),
      lagDays: 0,
    });
  }

  const predecessorsByTask = new Map<string, Relationship[]>();
  const successorsByTask = new Map<string, Relationship[]>();
  for (const rel of relationships) {
    const preds = predecessorsByTask.get(rel.successorId);
    if (preds) preds.push(rel);
    else predecessorsByTask.set(rel.successorId, [rel]);

    const succs = successorsByTask.get(rel.predecessorId);
    if (succs) succs.push(rel);
    else successorsByTask.set(rel.predecessorId, [rel]);
  }

  const assignmentsByTask = new Map<string, ResourceAssignment[]>();
  for (const el of elements(doc, 'ResourceAssignment')) {
    const taskId = text(el, 'ActivityObjectId');
    if (!taskId) continue;
    const assignment: ResourceAssignment = {
      resourceId: text(el, 'ResourceObjectId'),
      budgetUnits: num(text(el, 'PlannedUnits')),
      actualUnits: num(text(el, 'ActualUnits')),
      remainingUnits: num(text(el, 'RemainingUnits')),
      budgetCost: num(text(el, 'PlannedCost')),
      actualCost: num(text(el, 'ActualCost')),
      remainingCost: num(text(el, 'RemainingCost')),
    };
    const list = assignmentsByTask.get(taskId);
    if (list) list.push(assignment);
    else assignmentsByTask.set(taskId, [assignment]);
  }

  onProgress?.(65, 'Building activities');

  const activityEls = elements(doc, 'Activity');
  if (activityEls.length === 0) {
    throw new Error('No <Activity> elements found in the XML.');
  }

  const calendarUsage = new Map<string, number>();
  for (const el of activityEls) {
    const calId = text(el, 'CalendarObjectId');
    if (calId) calendarUsage.set(calId, (calendarUsage.get(calId) ?? 0) + 1);
  }
  let defaultHoursPerDay = 8;
  if (calendarUsage.size) {
    const [dominant] = [...calendarUsage.entries()].sort((a, b) => b[1] - a[1])[0];
    defaultHoursPerDay = calendars.get(dominant)?.hoursPerDay ?? 8;
  }

  const activities: Activity[] = activityEls.map((el) => {
    const id = text(el, 'ObjectId');
    const calendarId = text(el, 'CalendarObjectId') || null;
    const hoursPerDay = calendars.get(calendarId ?? '')?.hoursPerDay ?? defaultHoursPerDay;

    const status = STATUS_MAP[text(el, 'Status')] ?? 'Not Started';
    const type = TYPE_MAP[text(el, 'Type')] ?? 'Other';

    const targetStart = parseXerDate(
      text(el, 'PlannedStartDate', 'StartDate')
    );
    const targetFinish = parseXerDate(
      text(el, 'PlannedFinishDate', 'FinishDate')
    );
    const actualStart = parseXerDate(text(el, 'ActualStartDate'));
    const actualFinish = parseXerDate(text(el, 'ActualFinishDate'));

    const assignments = assignmentsByTask.get(id) ?? [];
    const budgetCost = assignments.reduce((sum, a) => sum + a.budgetCost, 0);
    const actualCost = assignments.reduce((sum, a) => sum + a.actualCost, 0);
    const remainingCost = assignments.reduce((sum, a) => sum + a.remainingCost, 0);

    const constraintType = toConstraint(text(el, 'PrimaryConstraintType'));
    const secondaryConstraintType = toConstraint(text(el, 'SecondaryConstraintType'));
    const totalFloatDays = num(text(el, 'TotalFloat')) / hoursPerDay;

    const physPct = text(el, 'PhysicalPercentComplete', 'PercentComplete');

    return {
      id,
      code: text(el, 'Id') || id,
      name: text(el, 'Name') || 'Unnamed activity',
      wbsId: text(el, 'WBSObjectId') || null,
      calendarId,
      type,
      isMilestone: type === 'Milestone' || type === 'Finish Milestone',

      status,
      isComplete: status === 'Completed',
      isInProgress: status === 'In Progress',
      isNotStarted: status === 'Not Started',

      targetStart,
      targetFinish,
      actualStart,
      actualFinish,
      earlyStart: parseXerDate(text(el, 'EarlyStartDate')),
      earlyFinish: parseXerDate(text(el, 'EarlyFinishDate')),
      lateStart: parseXerDate(text(el, 'LateStartDate')),
      lateFinish: parseXerDate(text(el, 'LateFinishDate')),

      start: actualStart ?? targetStart,
      finish: actualFinish ?? targetFinish,

      originalDurationDays: num(text(el, 'PlannedDuration')) / hoursPerDay,
      remainingDurationDays: num(text(el, 'RemainingDuration')) / hoursPerDay,
      totalFloatDays,
      freeFloatDays: num(text(el, 'FreeFloat')) / hoursPerDay,
      percentComplete: physPct
        ? num(physPct)
        : status === 'Completed'
          ? 100
          : status === 'In Progress'
            ? 50
            : 0,

      isCritical: totalFloatDays <= 0,
      isDrivingPath: text(el, 'IsCritical') === 'true',
      floatPath: null,

      constraintType,
      constraintDate: parseXerDate(text(el, 'PrimaryConstraintDate')),
      secondaryConstraintType,
      hasHardConstraint:
        HARD_CONSTRAINTS.includes(constraintType) ||
        HARD_CONSTRAINTS.includes(secondaryConstraintType),

      predecessors: predecessorsByTask.get(id) ?? [],
      successors: successorsByTask.get(id) ?? [],
      resources: assignments,

      budgetCost,
      actualCost,
      remainingCost,
      atCompletionCost: actualCost + remainingCost,
      budgetUnits: assignments.reduce((sum, a) => sum + a.budgetUnits, 0),
      actualUnits: assignments.reduce((sum, a) => sum + a.actualUnits, 0),

      activityCodes: {},
      hoursPerDay,
      raw: {},
    };
  });

  onProgress?.(88, 'Resolving dates');

  const activityById = new Map(activities.map((a) => [a.id, a]));
  for (const rel of relationships) {
    const successor = activityById.get(rel.successorId);
    rel.lagDays = rel.lagHours / (successor?.hoursPerDay ?? defaultHoursPerDay);
  }

  const warnings: string[] = [];
  let dataDate = parseXerDate(text(projectEl, 'DataDate', 'CurrentDataDate'));
  let dataDateInferred = false;
  if (!dataDate) {
    dataDateInferred = true;
    const actuals = activities
      .flatMap((a) => [a.actualStart, a.actualFinish])
      .filter((d): d is Date => !!d);
    dataDate = actuals.length
      ? new Date(Math.max(...actuals.map((d) => d.getTime())))
      : null;
    warnings.push(
      'No data date in the file — inferred from the latest actual date. Time-based metrics are approximate.'
    );
  }
  if (assignmentsByTask.size === 0) {
    warnings.push('No resource assignments — cost and earned value analysis unavailable.');
  }

  const starts = activities.map((a) => a.start).filter((d): d is Date => !!d);
  const finishes = activities.map((a) => a.finish).filter((d): d is Date => !!d);

  onProgress?.(100, 'Done');

  return {
    source: 'xml',
    fileName: file.name,
    projectId: text(projectEl, 'ObjectId') || null,
    projectName:
      text(projectEl, 'Name') ||
      text(projectEl, 'Id') ||
      file.name.replace(/\.xml$/i, ''),

    dataDate,
    dataDateInferred,
    plannedStart: parseXerDate(text(projectEl, 'PlannedStartDate')),
    scheduledFinish: parseXerDate(text(projectEl, 'ScheduledFinishDate')),

    start: starts.length ? new Date(Math.min(...starts.map((d) => d.getTime()))) : null,
    finish: finishes.length ? new Date(Math.max(...finishes.map((d) => d.getTime()))) : null,

    activities,
    activityById,
    relationships,
    wbs,
    wbsById: new Map(wbs.map((n) => [n.id, n])),
    resources,
    calendars,
    activityCodeTypes: [],

    defaultHoursPerDay,
    warnings,
  };
}
